import { backendApi } from "./backend.api";
import { IAddress, IProduct } from "shared/models/models";
import { getSpaceId } from "shared/utils/space";

interface IAddressQueryParams {
  store?: boolean;
  active?: boolean;
  category?: number;
  spaceId?: number;
}

interface stopListParams {
  id: number;
  products: number[];
}

const baseUrl = "/address";

interface IProductWithQuantity extends IProduct {
  quantity: number;
}
export const addressApi = backendApi.injectEndpoints({
  endpoints: (build) => {
    return {
      addresses: build.query<IAddress[], IAddressQueryParams>({
        query: (queryParams) => {
          const spaceId = getSpaceId();
          const params: IAddressQueryParams = { ...queryParams };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: baseUrl,
            method: "GET",
            params: params,
          };
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: "Addresses" as const, id })),
                "Addresses",
              ]
            : ["Addresses"],
      }),
      stores: build.query<IAddress[], IAddressQueryParams>({
        query: (queryParams) => {
          const spaceId = getSpaceId();
          const params: IAddressQueryParams = { ...queryParams };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: `${baseUrl}/stores`,
            method: "GET",
            params: params,
          };
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: "Addresses" as const, id })),
                "Addresses",
              ]
            : ["Addresses"],
      }),
      snacks: build.query<IAddress[], IAddressQueryParams>({
        query: (queryParams) => {
          const spaceId = getSpaceId();
          const params: IAddressQueryParams = { ...queryParams };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: `${baseUrl}/snack`,
            method: "GET",
            params: params,
          };
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: "Addresses" as const, id })),
                "Addresses",
              ]
            : ["Addresses"],
      }),
      getAddress: build.query<IAddress, number>({
        query: (id) => ({ url: `${baseUrl}/${id}`, method: "GET" }),
        transformErrorResponse: (response: { status: string | number }) =>
          response.status,
        providesTags: (item) => [{ type: "Addresses", id: item?.id }],
      }),

      getAddressProducts: build.query<IProductWithQuantity[], string>({
        query: (id) => ({ url: `${baseUrl}/${id}/products`, method: "GET" }),
        transformErrorResponse: (response: { status: string | number }) =>
          response.status,
      }),
      addAddress: build.mutation<IAddress, Partial<IAddress>>({
        query: (payload) => {
          const spaceId = getSpaceId();
          const params: Partial<IAddress> = { ...payload };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: baseUrl,
            method: "POST",
            data: params,
          };
        },
        invalidatesTags: ["Addresses"],
        transformResponse: (response: { data: IAddress }) => response.data,
      }),
      updateAddress: build.mutation<IAddress, Partial<IAddress>>({
        query: (payload) => ({
          url: `${baseUrl}/${payload.id}`,
          method: "PATCH",
          data: payload,
        }),
        invalidatesTags: (item) => [{ type: "Addresses", id: item?.id }],
      }),
      updateStopList: build.mutation<IAddress[], stopListParams>({
        query: (payload) => ({
          url: baseUrl + "/update-stop-list",
          method: "POST",
          data: payload,
        }),
        invalidatesTags: (result = []) => [
          ...result.map(({ id }) => ({ type: "Addresses", id: id }) as const),
        ],
        //
      }),
      updateMultiAddress: build.mutation<IAddress[], IAddress[]>({
        query: (payload) => ({
          url: baseUrl + "/update-multi",
          method: "POST",
          data: payload,
        }),
        invalidatesTags: (result = []) => [
          ...result.map(({ id }) => ({ type: "Addresses", id: id }) as const),
        ],
        // transformResponse: (response: { data: IVisit[] }) => response.data,
      }),
      deleteAddress: build.mutation<IAddress, number>({
        query: (id) => ({
          url: `${baseUrl}/${id}`,
          method: "DELETE",
          credentials: "include",
        }),
        invalidatesTags: ["Addresses"],
      }),
    };
  },
});

export const {
  useAddressesQuery,
  useSnacksQuery,
  useAddAddressMutation,
  useDeleteAddressMutation,
  useGetAddressQuery,
  useUpdateAddressMutation,
  useLazyGetAddressQuery,
  useUpdateMultiAddressMutation,
  useGetAddressProductsQuery,
  useUpdateStopListMutation,
} = addressApi;
